import { Component, computed, effect, ElementRef, inject, input, OnDestroy, viewChild } from "@angular/core";
import { WeatherMeasurement } from "@models";
import { MediaService } from "@services";

import { CategoryScale, Chart, ChartDataset, Legend, LinearScale, LineController, LineElement, PointElement, ScaleOptions, Tooltip } from "chart.js";

Chart.register([
  CategoryScale,
  LinearScale,
  LineElement,
  LineController,
  PointElement,
  Tooltip,
  Legend
]);

@Component({
  selector: "app-troll-chart",
  templateUrl: "./chart.component.html",
  styleUrls: ["./chart.component.scss"],
  imports: [
  ]
})
export class TrollChartComponent implements OnDestroy {
  mediaService = inject(MediaService);

  chartRef = viewChild.required<ElementRef<HTMLCanvasElement>>("chartElement");
  measurements = input.required<WeatherMeasurement[]>();
  datasets = input(["windDirectionTrue", "pressure"]);

  chart: Chart | null = null;

  datasetMap = computed(() => {
    const data = this.measurements() as unknown[];
    const themeColors = this.mediaService.themeColors();

    return {
      windDirectionTrue: {
        label: "Wind direction (°, true)",
        data: data,
        parsing: {
          xAxisKey: "measuredString",
          yAxisKey: "windDirectionTrue",
        },
        yAxisID: "windDirection",
        pointRadius: 1,
        pointHoverRadius: 4,
        pointHoverBorderColor: themeColors.error,
        pointHoverBackgroundColor: themeColors.error,
        showLine: false,
      },
      windDirectionMagnetic: {
        label: "Wind direction (°, magnetic)",
        data: data,
        parsing: {
          xAxisKey: "measuredString",
          yAxisKey: "windDirectionMagnetic",
        },
        yAxisID: "windDirection",
        pointRadius: 1,
        pointHoverRadius: 4,
        pointHoverBorderColor: themeColors.error,
        pointHoverBackgroundColor: themeColors.error,
        showLine: false,
      },
      windSpeedMs: {
        label: "Wind speed (m/s)",
        data: data,
        parsing: {
          xAxisKey: "measuredString",
          yAxisKey: "windSpeedMs",
        },
        yAxisID: "windSpeed",
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBorderColor: themeColors.error,
        pointHoverBackgroundColor: themeColors.error,
        showLine: true,
      },
      windSpeedKnots: {
        label: "Wind speed (knots)",
        data: data,
        parsing: {
          xAxisKey: "measuredString",
          yAxisKey: "windSpeedKnots",
        },
        yAxisID: "windSpeed",
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBorderColor: themeColors.error,
        pointHoverBackgroundColor: themeColors.error,
        showLine: true,
      },
      windGustMs: {
        label: "Wind gust (m/s)",
        data: data,
        parsing: {
          xAxisKey: "measuredString",
          yAxisKey: "windGustMs",
        },
        yAxisID: "windSpeed",
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBorderColor: themeColors.error,
        pointHoverBackgroundColor: themeColors.error,
        showLine: true,
      },
      windGustKnots: {
        label: "Wind gust (knots)",
        data: data,
        parsing: {
          xAxisKey: "measuredString",
          yAxisKey: "windGustKnots",
        },
        yAxisID: "windSpeed",
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBorderColor: themeColors.error,
        pointHoverBackgroundColor: themeColors.error,
        showLine: true,
      },
      airTemperature: {
        label: "Air temperature (°C)",
        data: data,
        parsing: {
          xAxisKey: "measuredString",
          yAxisKey: "airTemperature",
        },
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBorderColor: themeColors.error,
        pointHoverBackgroundColor: themeColors.error,
        showLine: true,
      },
      airHumidity: {
        label: "Relative humidity (%)",
        data: data,
        parsing: {
          xAxisKey: "measuredString",
          yAxisKey: "airHumidity",
        },
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBorderColor: themeColors.error,
        pointHoverBackgroundColor: themeColors.error,
        showLine: true,
      },
      dewPoint: {
        label: "Dew point (°C)",
        data: data,
        parsing: {
          xAxisKey: "measuredString",
          yAxisKey: "dewPoint",
        },
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBorderColor: themeColors.error,
        pointHoverBackgroundColor: themeColors.error,
        showLine: true,
      },
      pressure: {
        label: "Pressure (hPa)",
        data: data,
        parsing: {
          xAxisKey: "measuredString",
          yAxisKey: "pressure",
        },
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBorderColor: themeColors.error,
        pointHoverBackgroundColor: themeColors.error,
        showLine: true,
      },
    } as Record<string, ChartDataset>;
  });

  constructor() {
    effect(() => {
      const canvas = this.chartRef().nativeElement;
      const map = this.datasetMap();
      const themeColors = this.mediaService.themeColors();
      const colors = [
        themeColors.primary,
        themeColors.text,
        themeColors.border,
        themeColors.error,
      ];
      const datasets = this.datasets().map((d, i) => {
        const colorIndex = i < colors.length ? i : colors.length - 1;
        return {
          ...map[d],
          borderColor: colors[colorIndex],
          backgroundColor: colors[colorIndex],
        } as ChartDataset;
      });

      this.chart?.destroy();

      this.chart = new Chart(canvas, {
        type: "line",
        data: {
          datasets: datasets,
        },
        options: {
          maintainAspectRatio: false,
          interaction: {
            mode: "index",
            intersect: false, // allow hovering anywhere on the x-axis
          },
          transitions: {
            active: {
              animation: {
                duration: 0, // remove transitional animations
              },
            },
          },
          scales: {
            x: {
              reverse: true, // data is ordered by date (desc)
              ticks: {
                autoSkip: false,
                callback: function(value) {
                  const label = this.getLabelForValue(value as number);
                  const date = new Date(label);

                  if (date.getUTCMinutes() === 0) {
                    const hour = date.getUTCHours();
                    if (hour % 12 === 0) {
                      return label; // print labels for every 6 hours
                    }
                  }

                  return null;
                },
              },
            },
            y: {
              display: 'auto',
            },
            windDirection: {
              type: "linear",
              position: "right",
              display: 'auto',
              title: {
                display: true,
                text: "Wind direction (°)",
              },
              min: 0,
              max: 360,
              ticks: {
                stepSize: 90,
              },
              grid: {
                drawOnChartArea: false,
              },
            },
            windSpeed: {
              type: 'linear',
              position: 'left',
              display: 'auto',
              title: {
                display: true,
                text: 'Wind speed',
              },
              beginAtZero: true,
            } as ScaleOptions,
          },
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }
}
